import React, { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { Button } from "../ui/button";
import { Download, PenLine, CheckCircle2, ExternalLink, RefreshCw, Clock, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const LEVEL_LABEL = {
  electronic_signature: { fr: "SES", en: "SES", color: "slate" },
  advanced_electronic_signature: { fr: "AES eIDAS", en: "AES eIDAS", color: "emerald" },
  qualified_electronic_signature: { fr: "QES eIDAS", en: "QES eIDAS", color: "emerald" },
};

export default function ContractsWidget() {
  const { lang } = useI18n();
  const [contracts, setContracts] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/contracts").then((r) => setContracts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // gentle auto-refresh for status updates
    return () => clearInterval(id);
  }, []);

  const launchSign = async (c) => {
    setBusyId(c.contract_id);
    try {
      const { data } = await api.post(`/contracts/${c.contract_id}/sign`);
      const url = data.signing_url;
      if (url) {
        // Open Yousign in a new tab — user signs there, returns to dashboard
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success(lang === "fr"
          ? "Page Yousign ouverte. Signez puis revenez ici (cliquer Actualiser pour voir le statut)."
          : "Yousign page opened. Sign then return here.");
        load();
      } else if (data.status === "signed") {
        toast.success(lang === "fr" ? "Contrat signé." : "Contract signed.");
        load();
      } else {
        toast.error(lang === "fr" ? "Lien Yousign indisponible." : "Yousign link unavailable.");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 409 && detail === "missing_phone_number") {
        toast.error(lang === "fr"
          ? "Numéro de téléphone manquant. Renseignez-le dans votre profil avant de signer."
          : "Phone number missing. Add it to your profile before signing.");
      } else if (status === 502) {
        toast.error(lang === "fr"
          ? `Yousign indisponible. ${detail.startsWith("yousign_error") ? "Erreur : " + detail.slice(15) : "Réessayez dans quelques secondes."}`
          : "Yousign unavailable.");
      } else {
        toast.error(detail || "Erreur");
      }
      load(); // refresh state to reveal blocked_reason
    } finally {
      setBusyId(null);
    }
  };

  const refresh = async (c) => {
    setBusyId(c.contract_id);
    try {
      const { data } = await api.post(`/contracts/${c.contract_id}/refresh`);
      if (data.status === "signed") {
        toast.success(lang === "fr" ? "Contrat signé !" : "Contract signed!");
      } else {
        toast.info(lang === "fr" ? "Pas encore signé chez Yousign." : "Not yet signed at Yousign.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="contracts-widget">
      <div className="flex items-center justify-between mb-5">
        <div className="label-divider !text-slate-800">
          {lang === "fr" ? "Contrats SPV · Signature Yousign" : "SPV Contracts · Yousign Signature"}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-emerald-700 font-bold">
          <ShieldCheck className="h-3 w-3" /> eIDAS
        </div>
      </div>

      {contracts.length === 0 ? (
        <div className="text-sm text-slate-500 py-4">{lang === "fr" ? "Aucun contrat." : "No contracts."}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {contracts.map((c) => {
            const level = LEVEL_LABEL[c.signature_level] || null;
            const isPending = c.status === "pending_signature";
            const isSigned = c.status === "signed";
            const isExpired = c.status === "expired" || c.status === "declined";
            const hasYousign = !!c.signature_request_id;
            const blocked = c.yousign_blocked_reason === "missing_phone_number";

            return (
              <li key={c.contract_id} className="py-4 flex items-start justify-between gap-3" data-testid={`contract-row-${c.contract_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.spv_name}</div>
                    {level && (
                      <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border ${
                        level.color === "emerald"
                          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                          : "bg-slate-100 border-slate-200 text-slate-700"
                      }`}>
                        {level[lang] || level.fr}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-2">
                    <span className="font-mono">{Number(c.amount).toLocaleString()}€</span>
                    <span>·</span>
                    {isSigned && (
                      <span className="text-emerald-700 font-medium inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> {lang === "fr" ? "Signé" : "Signed"}
                      </span>
                    )}
                    {isPending && hasYousign && (
                      <span className="text-amber-700 font-medium inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {lang === "fr" ? "Signature en cours chez Yousign" : "Signature pending on Yousign"}
                      </span>
                    )}
                    {isPending && !hasYousign && !blocked && (
                      <span className="text-slate-700 font-medium">
                        {lang === "fr" ? "À signer" : "To sign"}
                      </span>
                    )}
                    {blocked && (
                      <span className="text-red-700 font-medium inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> {lang === "fr" ? "Numéro de téléphone requis" : "Phone number required"}
                      </span>
                    )}
                    {isExpired && (
                      <span className="text-slate-500 font-medium">
                        {c.status === "declined" ? (lang === "fr" ? "Refusé" : "Declined") : (lang === "fr" ? "Expiré" : "Expired")}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  {isPending && !hasYousign && !blocked && (
                    <Button
                      size="sm" className="rounded-sm h-8 text-xs bg-slate-900 hover:bg-slate-800"
                      onClick={() => launchSign(c)}
                      disabled={busyId === c.contract_id}
                      data-testid={`contract-sign-${c.contract_id}`}
                    >
                      <PenLine className="h-3.5 w-3.5 mr-1.5" />
                      {busyId === c.contract_id ? "…" : (lang === "fr" ? "Signer" : "Sign")}
                    </Button>
                  )}
                  {isPending && hasYousign && c.signing_url && (
                    <a
                      href={c.signing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center text-xs h-8 px-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm gap-1.5"
                      data-testid={`contract-yousign-link-${c.contract_id}`}
                    >
                      <ExternalLink className="h-3 w-3" /> {lang === "fr" ? "Ouvrir Yousign" : "Open Yousign"}
                    </a>
                  )}
                  {isPending && hasYousign && (
                    <Button
                      size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                      onClick={() => refresh(c)}
                      disabled={busyId === c.contract_id}
                      data-testid={`contract-refresh-${c.contract_id}`}
                    >
                      <RefreshCw className={`h-3 w-3 ${busyId === c.contract_id ? "animate-spin" : ""}`} />
                    </Button>
                  )}
                  {isSigned && c.signed_pdf_b64 ? (
                    <a
                      href={`${API}/contracts/${c.contract_id}/signed-pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center text-xs h-8 px-3 border border-emerald-700 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-1.5"
                      data-testid={`contract-signed-pdf-${c.contract_id}`}
                    >
                      <Download className="h-3 w-3" /> {lang === "fr" ? "PDF signé" : "Signed PDF"}
                    </a>
                  ) : (
                    <a
                      href={`${API}/contracts/${c.contract_id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center text-xs h-8 px-2.5 border border-slate-300 rounded-sm hover:bg-slate-50 text-slate-700"
                      data-testid={`contract-pdf-${c.contract_id}`}
                    >
                      <Download className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-5 pt-4 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed">
        <strong className="text-slate-700">eIDAS :</strong> {lang === "fr"
          ? "AES (Avancée) ≤ 50 000€ · QES (Qualifiée) ≥ 50 000€. Signature SMS deux facteurs. Conservation 10 ans (art. L.123-24)."
          : "AES below €50K · QES at/above €50K. Two-factor SMS signature. 10-year archival (art. L.123-24)."}
      </div>
    </div>
  );
}
